import React from 'react';
import { Eye, X } from 'lucide-react';
import { getImpersonation, clearImpersonation } from '@/lib/impersonation';

/**
 * Phase 2E.2 — the "you are not yourself right now" strip.
 *
 * Renders nothing when not impersonating, so it can be mounted
 * unconditionally in Layout.
 *
 * WHY THE COPY IS SO SHORT
 * ------------------------
 * Phases 2E.2a–c converted AI Employees (13/15) and the AI Employee
 * catalog (11/11), bringing impersonation coverage to 536/757
 * tenant-data handlers. The practical consequence is that everything a
 * tenant can see, you now see as them — so the banner does not need a
 * caveat list explaining which screens are and aren't covered.
 *
 * One qualifier survives, and it is deliberate. 34 personal-data
 * handlers (ara_sessions, chat, chat_uploads, certifications, ara_v3_*)
 * are excluded by design: each filters on
 * `user_id = current_user.user_id`, so converting them would either be
 * inert or a privacy escalation — a root admin reading a customer's
 * staff's chat history. Verified manually on 19 Aug against Dexter
 * Laboratories: GET /api/ara-sessions while impersonating returned the
 * acting user's own sessions, all stamped ten_simplimeta_root. The
 * reasoning is in middleware/tenant_scoping.py under "What
 * impersonation deliberately does NOT cover" and pinned by
 * tests/test_impersonation_coverage_boundary.py.
 *
 * SSE streams reach the same outcome by a different route: they scope
 * on `user.tenant_id` because EventSource cannot send custom headers,
 * so chat streaming stays yours regardless of this banner.
 *
 * WHY EXIT RELOADS
 * ----------------
 * Every mounted page is holding data fetched as the impersonated
 * tenant. Clearing the header without discarding that data would leave
 * one tenant's rows on screen with no banner over them, which is the
 * exact failure this component exists to prevent. A hard reload is
 * blunt but it cannot half-work.
 */
export default function ImpersonationBanner() {
  const impersonation = getImpersonation();
  if (!impersonation) return null;

  const handleExit = () => {
    clearImpersonation();
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="impersonation-banner"
      className="shrink-0 flex items-center gap-3 px-4 py-2 bg-amber-500/15 border-b border-amber-500/40 text-amber-900 dark:text-amber-200"
    >
      <Eye size={16} className="shrink-0" />
      <div className="min-w-0 flex-1 text-sm">
        <span className="font-semibold">
          Acting as {impersonation.name}
        </span>
        <span className="hidden sm:inline opacity-80">
          {' '}— AI Chat shows your own sessions, not theirs.
        </span>
      </div>
      <button
        type="button"
        onClick={handleExit}
        data-testid="impersonation-exit"
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-amber-500/25 hover:bg-amber-500/40 transition-colors"
      >
        <X size={13} />
        Exit
      </button>
    </div>
  );
}
